import React from 'react';
import { X, Calendar, CreditCard, CheckCircle } from 'lucide-react';

const PaymentHistoryModal = ({ category, formatCurrency, onClose }) => {
  const paymentHistory = category?.paymentDetails || [];
  const totalPaid = category?.paid || 0;
  const totalAmount = category?.amount || 0;
  const remaining = totalAmount - totalPaid;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Payment History</h3>
            <p className="text-xs text-gray-500 mt-0.5">{category?.categoryName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary */}
        <div className="p-4 border-b" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] text-gray-600 mb-0.5">Total Amount</p>
              <p className="text-base font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-600 mb-0.5">Total Paid</p>
              <p className="text-base font-bold text-green-600">{formatCurrency(totalPaid)}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-600 mb-0.5">Remaining</p>
              <p className="text-base font-bold text-red-600">{formatCurrency(remaining)}</p>
            </div>
          </div>
        </div>

        {/* Payment List */}
        <div className="flex-1 overflow-y-auto p-4">
          {paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No payment history available</p>
            </div>
          ) : (
            <div className="space-y-2">
              {paymentHistory.map((payment, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded p-3 hover:border-green-300 hover:bg-green-50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-1.5 bg-green-100 rounded">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-bold text-green-600">
                            {formatCurrency(payment.amount)}
                          </p>
                          {payment.paymentGateway === 'Razorpay' ? (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded border" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)', color: 'var(--primary-color, #4f46e5)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 30%, white)' }}>
                                Online
                              </span>
                          ) : (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)', color: 'var(--primary-color, #4f46e5)' }}>
                                {payment.mode || payment.paymentMode || 'N/A'}
                              </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>
                              {payment.date 
                                ? new Date(payment.date).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })
                                : 'N/A'}
                            </span>
                          </div>
                          
                          {(payment.transactionId || payment.tid) && (
                            <div className="flex items-center gap-1" title="Transaction ID">
                              <CreditCard className="h-3.5 w-3.5 text-gray-400" />
                              <span className="font-mono text-[10px] text-gray-600 bg-gray-100 px-1 rounded">
                                {payment.transactionId || payment.tid}
                              </span>
                            </div>
                          )}
                        </div>

                        {payment.paymentGateway && (
                          <div className="mt-1 flex items-center gap-1">
                            <span className="text-[10px] text-gray-500">
                              via <span className="font-medium text-gray-700">{payment.paymentGateway}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              Total Transactions: <span className="font-semibold text-gray-900">{paymentHistory.length}</span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;
